import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { ActionDialog } from '@/common/dialog/ActionDialog';
import { useDialog } from '@/common/dialog/DialogContext';
import { ExportDialog } from '@/common/dialog/ExportDialog';
import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { TargetKpiSelect } from '@/common/entityFramework/entityPerspective/overwrite/TargetKpiSelect';
import { Grid } from '@/common/grid/Grid';
import { useDependentKey } from '@/common/hooks/key/useDependentKey';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { ContentBox } from '@/common/layout/ContentBox';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Split } from '@/common/split/Split';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TreeUtils } from '../../utils/TreeUtils';
import { EntityBanner } from '../entityBanner/EntityBanner';
import { useTab } from '../entityTabs/TabContext';
import { EntityTitle } from '../entityTitle/EntityTitle';
import { DimRearrangeButton } from './support/DimRearrangeButton';
import { DimTree } from './support/DimTree';
import { FilterSummary } from './support/FilterSummary';
import { ViewSelect } from './support/ViewSelect';

/**
 * FDN-244 (Perspective)
 */
export const EntityPerspective = ({
  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Views
  views,

  // Optional Modifications to EntityPerspective
  augReqParam,
  rowIconsFn,
  augButtons,
  defaultDimFilters: defaultDimFiltersProp = {},

  // Optional Modifications to the Grid
  gridOptions = {},
  events = {},

  // Optional Listener to the Reset View Button
  onViewReset,

  // Ref
  ref,
}) => {
  // Module and Entity
  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  // Context
  const { navState, assign, navigateTo } = useNavState();
  const { tabId } = useTab();
  const rsrc = useRsrc();

  // NavState
  const perspectiveNavStateId = tabId ? tabId + 'PerspectiveState' : 'perspectiveState';
  const perspectiveNavState = navState[perspectiveNavStateId] ?? {};

  // Ref
  const gridRef = useRef(null);

  // State
  const [activeView, setActiveView] = useState(
    perspectiveNavState.viewName ? views.find((view) => view.name === perspectiveNavState.viewName) : views[0],
  );
  const [activeDims, setActiveDims] = useState([]);
  const [perspectiveDef, setPerspectiveDef] = useState();

  const [fieldDefs, setFieldDefs] = useState();
  const [exportReq, setExportReq] = useState();
  const [entityDef, setEntityDef] = useState();
  const [dimFilters, setDimFilters] = useState({}); // Object of dimCode to a Set of selected Entity IDs for that dim
  const [dimToPeriodicities, setDimToPeriodicities] = useState(); // Object of dimCode to an array of selected Periodicities

  const [showFilterSummary, setShowFilterSummary] = useState(perspectiveNavState.showFilterSummary ?? false);
  const [splitSizes, setSplitSizes] = useState(perspectiveNavState.splitSizes ?? [75, 25]);

  // ----- Initialization -----

  useEffect(() => {
    let defPluginReq = rsrc.data(module, 'PERSPECTIVE_DEF_DATA_PLUGIN', { entity }, entity);
    let entityDefReq = rsrc.entityDef(module, entity);

    Promise.all([defPluginReq, entityDefReq]).then(([perspectiveDef, entityDef]) => {
      setPerspectiveDef(perspectiveDef);
      setEntityDef(entityDef);
    });
  }, []);

  useEffect(() => {
    if (!perspectiveDef) return;
    // Handle and set activeDims
    const disabledDims = activeView.disabledDims ?? [];
    Object.values(perspectiveDef.codeToDim).forEach((dim) => {
      dim.id = StringUtils.toLowerCamel(dim.code);
      if (disabledDims.includes(dim.code)) {
        dim.disabled = true;
      }
    });

    resetDims();
    resetPeriodicities();

    // Restore state if applicable
    if (perspectiveNavState.dimCodes) {
      setDims(perspectiveNavState.dimCodes);
    }

    if (perspectiveNavState.dimFilters) {
      const dimFilters = defaultDimFilters();
      setDimFilters({ ...dimFilters, ...perspectiveNavState.dimFilters });
    }

    if (perspectiveNavState.dimToPeriodicities) {
      setDimToPeriodicities(perspectiveNavState.dimToPeriodicities);
    }

    buildAndSetColumnDefs();
  }, [perspectiveDef]);

  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, entity);
    return () => {
      unsubscrToEntityChange(module, entity);
    };
  }, []);

  // ----- Column Definitions -----

  const buildAndSetColumnDefs = () => {
    const activeKpis = activeView.kpis.map((kpiCode) => perspectiveDef.codeToKpi[kpiCode]);

    const grpFieldDefs = {};
    const fieldDefs = [
      {
        displayName: '',
        colId: 'dimCol',
        field: 'node',
        suppressSizeToFit: true,
        valueFormatter: null,
        autoGrouping: true,
        cellRenderer: 'perspectiveDimCellRenderer',
        cellClass: 'align-left',
        pinned: 'left',
        filter: null,
        sortable: false,
        lockPosition: 'left',
        rowIconsFn, // Supply the rowIcons function in the colDef for the cellRenderer
      },
    ];

    activeKpis.forEach((kpi) => {
      kpi.field = StringUtils.toLowerCamel(kpi.code);

      if (kpi.cellRendererCode && kpi.cellRendererCode !== 'NONE') {
        kpi.cellRenderer = StringUtils.toLowerCamel(kpi.cellRendererCode) + 'CellRenderer';
      }

      kpi.filter = false;
      kpi.sortable = false;

      const grpName = kpi.grpName;
      if (grpName) {
        let grpFieldDef = grpFieldDefs[grpName];
        if (grpFieldDef == null) {
          grpFieldDef = { displayName: grpName, groupId: grpName, children: [] };
          grpFieldDefs[grpName] = grpFieldDef;
          fieldDefs.push(grpFieldDef);
        }
        grpFieldDef.children.push(kpi);
      } else {
        fieldDefs.push(kpi);
      }
    });

    setFieldDefs(fieldDefs);
  };

  // ----- Fetch -----

  const onGridReady = () => {
    serverSideDatasource();
  };

  const serverSideDatasource = () => {
    const dataSource = {
      getRows: (params) => {
        const reqParam = buildReqParam(params);
        if (reqParam.level === 0) {
          buildAndSetExportReq(reqParam);
        }

        return rsrc.data(module, 'PERSPECTIVE_FETCH_DATA_PLUGIN', reqParam, entity).then((res) => {
          params.success({ rowData: res, rowCount: res.length });
        });
      },
    };
    gridRef.current.setDataSource(dataSource);
  };

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module,
      entity,
      fetchReqParam: reqParam,
      activeDims: activeDims.map((dim) => dim.code),
      fieldDefs,
      uiType: 'PERSPECTIVE',
    };

    setExportReq(exportReq);
  };

  const isServerSideGroup = (node) => {
    return !node.isLeaf;
  };

  const getServerSideGroupKey = (node) => {
    return node.path;
  };

  const refreshGrid = () => {
    serverSideDatasource();
  };

  useEffect(() => {
    if (!gridRef.current || !gridRef.current.gridApiRef.current) return;
    refreshGrid();
  }, [refreshTrigger, dimFilters, dimToPeriodicities, activeDims, augReqParam]);

  const buildReqParam = useCallback(
    (params) => {
      const isFetch = !!params.parentNode;

      const reqParam = {};

      // Entity
      reqParam.entity = entity;

      // View
      reqParam.view = activeView.name;

      // Level
      // Parent node is the node we want the data underneath.
      // In the fetch scenario, parentNode is params.parentNode. However, in the navigation scenario, params itself is the parentNode
      // as this is the node we right clicked on to see its underlying transactions.
      const parentNode = isFetch ? params.parentNode : params.node;
      const firstFetch = params == null || parentNode == null || parentNode.data == null;
      const level = firstFetch ? 0 : parentNode.data.level + 1;
      reqParam.level = level;

      // Current Dim
      reqParam.currentDim = firstFetch ? activeDims[0].code : parentNode.data.nextDim;

      // Dim Order
      const dimCodes = activeDims.map((dim) => dim.code);
      reqParam.dimCodes = dimCodes;

      // The ~ delimited path of nodes. The children of this parent/path are being requested
      let wherePath = firstFetch ? '%' : parentNode.data.path + '~%';
      if (parentNode.data?.isLeaf) {
        // This can only be true in the scenario of navigating to the Viewer
        wherePath = wherePath.substring(0, wherePath.length - 2);
      }
      reqParam.wherePath = wherePath;

      // Periodicities
      reqParam.dimToPeriodicities = dimToPeriodicities;

      // Tree Filtering
      let expr = null;
      activeDims.forEach((dim) => {
        const selectedIds = dimFilters[dim.code];
        const { leafIds } = TreeUtils.parseTree(dim.tree);

        // If all IDs are selected, there is no need to filter
        if (selectedIds.length !== leafIds.length) {
          const treeExpr = ExprUtils.whereParams(StringUtils.toLowerCamel(dim.code) + 'Id', selectedIds);
          expr = expr ? ExprUtils.and(expr, treeExpr) : treeExpr;
        }
      });
      reqParam.expr = expr;

      // Sorting
      const sortModel = params.request?.sortModel;
      if (sortModel) {
        const userSorter = sortModel[0];
        reqParam.userSorter = userSorter;
      }

      if (augReqParam) {
        augReqParam(reqParam);
      }

      return reqParam;
    },
    [activeView, activeDims, dimFilters, dimToPeriodicities, augReqParam],
  );

  // ----- NavState -----

  const saveNavState = () => {
    if (!perspectiveDef) return;
    const newPerspectiveNavState = {
      viewName: activeView.name,
      dimCodes: activeDims.map((dim) => dim.code),
      dimFilters,
      dimToPeriodicities,
      showFilterSummary,
      splitSizes,
    };

    assign(perspectiveNavStateId, newPerspectiveNavState);
  };

  useEffect(() => {
    saveNavState();
  }, [activeView, activeDims, dimFilters, dimToPeriodicities, showFilterSummary, splitSizes]);

  // ----- View -----

  useEffect(() => {
    if (!perspectiveDef) return; // Only needs to be called when the view changes
    resetView();
  }, [activeView]);

  // FDN-53 (Reset View)
  const resetView = () => {
    gridRef.current.resetOpenedRowGroups();
    buildAndSetColumnDefs();
    resetDims();
    resetPeriodicities();

    if (onViewReset) {
      onViewReset();
    }
  };

  const changeView = (view) => {
    setActiveView(view);
  };

  // ----- Dimensions -----

  const setDims = (dimCodes) => {
    const prevDimCodes = activeDims.map((dim) => dim.code);

    const newDimFilters = {};
    dimCodes.forEach((dimCode) => {
      newDimFilters[dimCode] =
        prevDimCodes.includes(dimCode) ?
          dimFilters[dimCode]
        : TreeUtils.parseTree(perspectiveDef.codeToDim[dimCode].tree).leafIds;
    });

    setActiveDims(dimCodes.map((dimCode) => perspectiveDef.codeToDim[dimCode]));
    setDimFilters(newDimFilters);
  };

  const resetDims = () => {
    const activeDims = defaultDims();
    setActiveDims(activeDims);

    const dimFilters = defaultDimFilters();
    setDimFilters(dimFilters);
  };

  const defaultDims = () => {
    return activeView.dims.map((dimCode) => perspectiveDef.codeToDim[dimCode]);
  };

  const defaultDimFilters = () => {
    const dims = defaultDims();

    const dimFilters = {};
    dims.forEach((dim) => {
      const leafIds = defaultDimFiltersProp[dim.code] ?? TreeUtils.parseTree(dim.tree).leafIds;
      dimFilters[dim.code] = leafIds;
    });

    return dimFilters;
  };

  // ----- Periodicities -----

  const resetPeriodicities = () => {
    if (!activeView.dimToPeriodicities) return;

    const dimToPeriodicities = {};
    Object.entries(activeView.dimToPeriodicities).forEach(([dimCode, periodicities]) => {
      dimToPeriodicities[dimCode] = [periodicities[periodicities.length - 1]];
    });

    setDimToPeriodicities(dimToPeriodicities);
  };

  // ----- Context Menu Items -----

  const getContextMenuItems = (params) => {
    const contextMenuItems = [
      {
        name: 'Copy with Headers',
        action: () => copyWithHeaders(),
        icon: fas.faCopy,
      },
    ];

    return contextMenuItems;
  };

  const copyWithHeaders = () => {
    gridRef.current.gridApiRef.current.copySelectedRangeToClipboard({
      includeGroupHeaders: true,
      includeHeaders: true,
    });
  };

  const { openDialog } = useDialog();

  // ----- API to Parent -----

  // This had to be moved down due to the dependency on buildReqParam
  useImperativeHandle(ref, () => {
    return {
      getContextMenuItems,
      buildReqParam,
      codeToKpi: perspectiveDef?.codeToKpi,
      gridRef: gridRef,
      setDimFilter: (dimCode, ids) => {
        setDimFilters((prev) => ({ ...prev, [dimCode]: ids }));
      },
      navigateToViewer: (params, trxGroup, title, pluginCode = 'PERSPECTIVE_TRX_VIEWER_PLUGIN') => {
        const navState = {
          title,
          entity,
          pluginCode,
          viewerReqParam: {
            trxGroup,
            fetchReqParam: buildReqParam(params),
          },
        };
        navigateTo(`/${module}/perspective-trx-viewer`, navState);
      },
      navigateToLookupViewer: (params, leftTrxGroup, leftTitle, rightTrxGroup, rightTitle) => {
        const navState = {
          leftTitle,
          rightTitle,
          entity,
          reqParam: {
            leftTrxGroup: leftTrxGroup,
            rightTrxGroup: rightTrxGroup,
            fetchReqParam: buildReqParam(params),
          },
        };
        navigateTo(`/${module}/perspective-trx-lookup-viewer`, navState);
      },
      navigateToPerspectiveAdj: (params, trxGroup, title) => {
        const navState = {
          title,
          fetchReqParam: buildReqParam(params),
          trxGroup,
        };
        navigateTo(`/${module}/perspective-adj`, navState);
      },
      navigateToPivot: (params, trxGroup, title) => {
        const navState = {
          entity,
          fetchReqParam: buildReqParam(params),
          trxGroup,
          title,
        };
        navigateTo(`/${module}/perspective-pivot`, navState);
      },
      navigateToEntityList: (params, routeToEntity, navState) => {
        navState.entity = routeToEntity;
        navState.reqParam = { ...navState.reqParam, fetchReqParam: buildReqParam(params) };

        navigateTo(`/${module}/${StringUtils.toKebabConvention(routeToEntity)}`, navState);
      },
      overwrite: (params, kpiCode) => {
        const augFieldDefs = (fieldToFieldDef) => {
          fieldToFieldDef.targetKpi.component = (
            <TargetKpiSelect codeToKpi={perspectiveDef.codeToKpi} fieldDef={fieldToFieldDef.targetKpi} />
          );
        };

        const kpiDisplayName = perspectiveDef.codeToKpi[kpiCode].displayName;

        openDialog(ActionDialog, {
          data: {
            title: `${kpiDisplayName} Overwrite`,
            pluginCode: 'OVERWRITE_ACTION_PLUGIN',
            entity,
            augFieldDefs,
            addlReqData: {
              fetchReqParam: buildReqParam(params),
              adjKpi: kpiCode,
            },
          },
        });
      },
      openActionDialog: (params, title, pluginCode) => {
        openDialog(ActionDialog, {
          data: {
            title,
            pluginCode,
            entity,
            addlReqData: {
              fetchReqParam: buildReqParam(params),
              nodeData: params.node.data,
            },
          },
        });
      },
      openExportDialog: (data, displayName) => {
        openDialog(ExportDialog, {
          data: { ...buildReqParam(data), uiType: 'PERSPECTIVE_GRID', displayName },
        });
      },
    };
  }, [buildReqParam]);

  const gridKey = useDependentKey(fieldDefs);

  // ----- Rendering -----
  if (!perspectiveDef) return null;

  let buttons = [];
  if (views.length > 1) {
    buttons.push(<ViewSelect key="viewSelect" views={views} activeView={activeView} onViewChange={changeView} />);
  }
  if (augButtons) augButtons(buttons);
  buttons.push(
    <TitleButton
      key="filterSummaryButton"
      dataTestId="filter-summary-button"
      icon={far.faFilter}
      toolTip="Open Filter Summary"
      onClick={() => setShowFilterSummary((prev) => !prev)}
    />,
  );
  if (activeView.dimsRearrangeable ?? true) {
    buttons.push(
      <DimRearrangeButton
        key="dimRearrangeButton"
        setDims={setDims}
        codeToDim={perspectiveDef.codeToDim}
        activeDims={activeDims}
        numFrozenDims={activeView.numFrozenDims}
      />,
    );
  }
  buttons.push(
    <TitleButton
      key="resetViewButton"
      dataTestId="reset-view-button"
      icon={far.faWindowRestore}
      toolTip="Reset View"
      onClick={resetView}
    />,
  );

  buttons = [...buttons, <ExportButton key="export" exportReq={exportReq} />];

  return (
    <ContentBox>
      <EntityTitle buttons={buttons} />
      <EntityBanner />
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Dimension Filter Trees */}
        {activeDims.map((dim) => {
          const dimCode = dim.code;
          const selectedIds = dimFilters[dimCode];

          return (
            <DimTree
              key={dim.id}
              displayName={dim.displayName}
              disabled={dim.disabled}
              treeData={dim.tree}
              selectedIds={selectedIds}
              onFilterChange={(ids) => {
                setDimFilters((prev) => ({ ...prev, [dimCode]: ids }));
                events?.onDimFilterChange?.({ dimCode, ids });
              }}
              periodicities={activeView.dimToPeriodicities ? activeView.dimToPeriodicities[dim.code] : undefined}
              selectedPeriodicities={dimToPeriodicities ? dimToPeriodicities[dim.code] : undefined}
              onPeriodicityChange={(periodicities) =>
                setDimToPeriodicities((prev) => ({ ...prev, [dimCode]: periodicities }))
              }
            />
          );
        })}
      </Box>
      {fieldDefs && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Split
            direction="horizontal"
            initialSplit={splitSizes[0]}
            minSize={200}
            onDragEnd={({ 0: left, 1: right }) => setSplitSizes([left, right])}
            showSecondChild={showFilterSummary}
          >
            <Grid
              ref={gridRef}
              key={gridKey}
              gridOptions={{
                fieldDefs: fieldDefs,
                statusBar: null,
                rowModelType: 'serverSide',
                treeData: true,
                isServerSideGroup,
                getServerSideGroupKey,
                getContextMenuItems: getContextMenuItems,
                ...gridOptions,
              }}
              events={{
                ...events,
                onGridReady: onGridReady,
              }}
            />
            <FilterSummary activeDims={activeDims} dimFilters={dimFilters} showFilterSummary={showFilterSummary} />
          </Split>
        </Box>
      )}
    </ContentBox>
  );
};
